import { useSortable } from '@dnd-kit/sortable';
import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import { ChevronDownIcon, ChevronRightIcon, FolderIcon, FolderMinusIcon, FolderPlusIcon, TrashIcon, ArrowDownTrayIcon, PencilIcon, BoltIcon } from '@heroicons/react/24/outline';
import { memo, MouseEventHandler, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ContextMenu } from '../../../../components/ContextMenu';
import { SelectItemContent } from '../../../../components/selects/SelectItem';
import { useDeleteConfirmation } from '../../../../contexts/DeleteConfirmationContext';
import { RendererContext } from '../../../../contexts/RendererContext';
import { collectLeafAnnotations, isAnnotationContextGroup } from '../../../../contexts/AnnotationContext';
import { useShouldHideAnnotationType, useShouldHideGroupType } from '../../../../hooks/potree/useShouldHideAnnotationType';
import { useIsMultiSelect } from '../../../../hooks/modules/renderer/useIsMultiSelect';
import { useRename } from '../../../../hooks/modules/renderer/useRename';
import { useUpdatePosition } from '../../../../hooks/modules/renderer/useUpdatePosition';
import { useDeleteAnnotation } from '../../../../hooks/potree/useDeleteAnnotation';
import { useGroupOperations } from '../../../../hooks/potree/useGroupOperations';
import { useSelector } from 'react-redux';
import { T } from '../../../../translation/src';
import { GroupType, Project } from '../../../../types/graphqlTypes';
import { Annotation } from './Annotation';
import { AnnotationsIconsWrapper } from './AnnotationsIconsWrapper';
import { AnnotationsWithIndicesGroup } from './utils/resolveDropPosition';
import type { DropIndicator } from './index';
import { useExportDXF } from '../../../../hooks/potree/useExportDXF';
import { useExportIFC } from '../../../../hooks/potree/useExportIFC';
import { DropIndicatorLine } from './DropIndicatorLine';
import { GROUP_ICONS_BY_TYPE, type AnnotationTypeName } from '../../../../utils/annotationIcons';
import { MultiSelectMenuItems } from './MultiSelectMenuItems';
import { getAnnotationVisibleProperty, getFloorPlanePositionProperty, getRoomGroupPositionProperty } from '@pointorama/potree-shared';
import { v4 as uuid } from 'uuid';
import { manualRoomFolderId } from '../../../../utils/roomFolders';
import OrthoviewIcon from '../../../../assets/icons/orthoview.svg?react';
import { useViewManager } from '../../../../hooks/useViewManager';
import { useToggleHide } from '../../../../hooks/modules/project/useToggleHide';
import { usePropertyValue } from '../../../Properties/reactive/usePropertyValue';
import { Pill } from '../../../../components/Pill';
import { resolveFloorOutlineRings } from '../../../../potree/utils/skeleton/outlineRings';

const ContextMenuItems_: React.FC2<{
  group: AnnotationsWithIndicesGroup & { index: number };
  isFirst?: boolean;
  isLast?: boolean;
  groupIdentifier?: string;
  onToggleNameEditable?: () => void;
  rootIndex: number;
}> = ({ isLast, isFirst, group, rootIndex, onToggleNameEditable }) => {
  const { deleteSelected } = useDeleteAnnotation();
  const { ungroup } = useGroupOperations();
  const [exportDXF, onCanExportDXF] = useExportDXF();
  const [exportIFC, onCanExportIFC] = useExportIFC();
  const { requestDeleteConfirmation, requiresConfirmation } = useDeleteConfirmation();
  const { viewer } = useContext(RendererContext);
  const { in2DMode, toggle2DMode, planeId: planeId2DMode } = useViewManager();
  const { toggleHide } = useToggleHide();
  const reactiveVisible = usePropertyValue(getAnnotationVisibleProperty(group.identifier));

  // Surface groups behave like the former plane annotation: they can be entered
  // as an aligned 2D view. Mirrors the gating in the annotation context menu.
  const isSurfaceGroup = group.type === GroupType.Surface;
  const is2DModeAvailableInPersona = useMemo(() => {
    if (!isSurfaceGroup || !viewer?.personaManager) return false;
    const activePersona = viewer.personaManager.getActivePersona();
    if (activePersona?.name === 'floorplan') return false;
    const views = viewer.personaManager.viewManager?.getAvailableViews() || [];
    return views.some((view: { cameraMode?: number }) => view.cameraMode === 0);
  }, [isSurfaceGroup, viewer]);
  const is2DContext = isSurfaceGroup && in2DMode && planeId2DMode === group.identifier;

  const onToggle2DMode = useCallback(() => {
    toggle2DMode(group.identifier);
    toggleHide({ identifiers: [group.identifier], visible: reactiveVisible !== false });
  }, [toggle2DMode, toggleHide, group.identifier, reactiveVisible]);

  const [{ UpdatePositionItems }] = useUpdatePosition({
    groupIdentifier: group.identifier,
    downDisabled: isLast,
    upDisabled: isFirst,
  });

  const onDeleteGroup = useCallback(() => {
    // Room folders dissolve: deleteSelected writes the reconciliation
    // tombstone and the server re-parents the children to the floor (the
    // room measure and sketches are never deleted).
    deleteSelected([{ identifier: group.identifier, type: 'group' }]);
  }, [deleteSelected, group.identifier]);

  const onDeleteAnnotationWithConfirm = useCallback(() => {
    // Room folders may reach the panel without their `type` populated (the
    // id scheme is authoritative: `roomgroup_*`, see utils/roomFolders) —
    // resolve the effective type so folder deletion always confirms.
    const effectiveType = group.type ?? (group.identifier.startsWith('roomgroup_') ? GroupType.Room : undefined);
    if (effectiveType && requiresConfirmation(effectiveType)) {
      requestDeleteConfirmation(onDeleteGroup);
    } else {
      onDeleteGroup();
    }
  }, [group.type, group.identifier, requiresConfirmation, requestDeleteConfirmation, onDeleteGroup]);

  const onUnGroup = useCallback(() => {
    ungroup(group.identifier);
  }, [ungroup, group.identifier]);

  // Leaf annotations only: a floor's children include room folders whose
  // sketches must export with the floor.
  const groupAnnotations = useMemo(() => collectLeafAnnotations(group), [group]);

  const onExportAnnotationsDXF = useCallback(() => {
    exportDXF(groupAnnotations);
  }, [exportDXF, groupAnnotations]);

  const onExportAnnotationsIFC = useCallback(() => {
    exportIFC(groupAnnotations);
  }, [exportIFC, groupAnnotations]);

  const dxfExportPossible = useMemo(() => {
    return onCanExportDXF(groupAnnotations);
  }, [onCanExportDXF, groupAnnotations]);

  const ifcExportPossible = useMemo(() => {
    return onCanExportIFC(groupAnnotations);
  }, [onCanExportIFC, groupAnnotations]);

  // Room folders dissolve via delete (children re-parent to the floor), so a
  // separate ungroup would be a confusing duplicate.
  const ungroupPossible = group.annotations.length > 0 && group.type !== GroupType.Floor && group.type !== GroupType.Surface && group.type !== GroupType.Room;

  // Generate Walls (floors only): re-use the LayoutPersona's bootstrap
  // path on a single floor. Gated to floors that actually have an
  // outline annotation — the path needs an outline to scope the
  // skeleton's wall-region envelope.
  const isFloor = group.type === GroupType.Floor;
  const generateWallsPossible = useMemo(() => {
    if (!isFloor || !viewer?.scene) return false;
    // Merged sketch outline first, legacy OUTLINE annotation fallback — the
    // generation envelope resolves the same way (see generateWallsForFloor).
    return resolveFloorOutlineRings(viewer, group.identifier).length > 0;
  }, [isFloor, viewer, group.identifier]);

  const onGenerateWalls = useCallback(() => {
    if (!viewer?.personaManager) return;
    const layoutPersona = viewer.personaManager.getAllPersonas().find((p: { name: string }) => p.name === 'layout') as { generateWallsForFloor?: (id: string) => Promise<boolean> } | undefined;
    layoutPersona?.generateWallsForFloor?.(group.identifier);
  }, [viewer, group.identifier]);

  // Manual room folder (no linked room measure — never auto-dissolved). The
  // anchor seeds at the floor's plane position so the tag is immediately
  // visible and grabbable; the user drags it into a room from there.
  const { createGroup } = useGroupOperations();
  const onAddRoomFolder = useCallback(() => {
    const folderId = manualRoomFolderId(uuid());
    void createGroup({ identifier: folderId, name: 'Room', type: GroupType.Room, annotationIdentifiers: [], parentIdentifier: group.identifier })
      .then(() => {
        try {
          const floorPosition = getFloorPlanePositionProperty(group.identifier).value;
          if (floorPosition) getRoomGroupPositionProperty(folderId).value = { ...floorPosition };
        } catch {
          // property service not ready — the tag stays unplaced until dragged
        }
      })
      .catch(() => {
        // createGroup already restored server truth via refetch on failure
      });
  }, [createGroup, group.identifier]);

  return (
    <>
      <div className="flex flex-col">
        <SelectItemContent icon={PencilIcon} onClick={onToggleNameEditable}>
          <T _str="rename" swc />
        </SelectItemContent>
      </div>
      <div className="flex flex-col">
        <UpdatePositionItems index={rootIndex} />
      </div>
      <div>
        <SelectItemContent icon={FolderMinusIcon} onClick={onUnGroup} disabled={!ungroupPossible}>
          <T _str="ungroup" swc />
        </SelectItemContent>
      </div>
      {isFloor && (
        <div>
          <SelectItemContent icon={BoltIcon} onClick={onGenerateWalls} disabled={!generateWallsPossible}>
            <span className="flex flex-col">
              <T _str="generate walls" swc />
              {!generateWallsPossible && (
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  <T _str="draw an outer wall first" swc />
                </span>
              )}
            </span>
          </SelectItemContent>
          <SelectItemContent icon={FolderPlusIcon} onClick={onAddRoomFolder}>
            <T _str="add room" swc />
          </SelectItemContent>
        </div>
      )}
      {isSurfaceGroup && is2DModeAvailableInPersona && (
        <div className="flex flex-col">
          <SelectItemContent icon={OrthoviewIcon} onClick={onToggle2DMode}>
            {is2DContext ? <T _str="disable 2D mode" swc /> : <T _str="2D mode" swc />}
          </SelectItemContent>
        </div>
      )}
      <div>
        <SelectItemContent icon={ArrowDownTrayIcon} onClick={onExportAnnotationsDXF} disabled={!dxfExportPossible}>
          <T _str="export to DXF" swc />
        </SelectItemContent>
        <SelectItemContent icon={ArrowDownTrayIcon} onClick={onExportAnnotationsIFC} disabled={!ifcExportPossible}>
          <T _str="export to IFC" swc />
        </SelectItemContent>
      </div>
      <div className="flex flex-col">
        <SelectItemContent icon={TrashIcon} onClick={onDeleteAnnotationWithConfirm}>
          <T _str="delete" swc />
        </SelectItemContent>
      </div>
    </>
  );
};
const ContextMenuItems = memo(ContextMenuItems_);

interface AnnotationGroupProps {
  project?: Pick<Project, 'id'>;
  group: AnnotationsWithIndicesGroup & { index: number };
  isFirst?: boolean;
  isLast?: boolean;
  onClickAnnotation: (args1: { identifier: string; groupIdentifier?: string; type: 'group' | 'annotation' }, args2: { isCtrl?: boolean; isShift?: boolean }) => void;
  rootIndex: number;
  isReadOnly: boolean;
  dropIndicator?: DropIndicator;
  isDragOver?: boolean;
  isOpen?: boolean;
  onGroupOpenChange?: (groupId: string, isOpen: boolean) => void;
  /** Threaded through to room children so they can render their nested openings. */
  openingsByRoomId?: Record<string, string[]>;
  openRooms?: Set<string>;
  onRoomOpenChange?: (roomId: string, isOpen: boolean) => void;
  /** Open state for ALL groups (root + nested room folders) — nested groups look themselves up here. */
  openGroups?: Set<string>;
  /** True when this group renders inside another group (a floor's room folder) — indents the row + children. */
  nested?: boolean;
}

interface AnnotationGroupContentProps extends AnnotationGroupProps {
  sortableRef: (node: HTMLElement | null) => void;
  sortableTransition: string | undefined;
  sortableAttributes: DraggableAttributes;
  sortableListeners: DraggableSyntheticListeners;
  isDragging: boolean;
}

/** Inner content — does NOT call useSortable, so it won't re-render when the
 *  SortableContext items list changes. */
const AnnotationGroupContent = memo(
  ({
    project,
    group,
    isFirst,
    isLast,
    onClickAnnotation,
    rootIndex,
    isReadOnly,
    dropIndicator,
    isDragOver,
    isOpen: folderOpen = true,
    onGroupOpenChange,
    openingsByRoomId,
    openRooms,
    onRoomOpenChange,
    openGroups,
    nested = false,
    sortableRef,
    sortableTransition,
    sortableAttributes,
    sortableListeners,
    isDragging,
  }: AnnotationGroupContentProps) => {
    const { viewer } = useContext(RendererContext);
    const shouldHideType = useShouldHideAnnotationType();
    const shouldHideGroupType = useShouldHideGroupType();
    const { in2DMode, planeId: planeId2DMode } = useViewManager();
    const is2DContext = group.type === GroupType.Surface && in2DMode && planeId2DMode === group.identifier;
    const active = useSelector((state: { rendererProvider: { selectedAnnotations: { identifier: string }[] } }) => state.rendererProvider.selectedAnnotations.some((a) => a.identifier === group.identifier));
    const isMultiSelect = useIsMultiSelect() && active;
    const [{ onToggleNameEditable }, { nameEditable, nameInputComponent }] = useRename({
      name: group.name,
      groupIdentifier: group.identifier,
    });
    const onClick: MouseEventHandler<HTMLDivElement> = useCallback(
      (event) => {
        onClickAnnotation({ identifier: group.identifier, type: 'group' }, { isCtrl: event.ctrlKey || event.metaKey, isShift: event.shiftKey });
      },
      [group.identifier, onClickAnnotation],
    );

    const onMouseEnter: MouseEventHandler<HTMLDivElement> = useCallback(() => {
      if (group.type === GroupType.Floor) {
        viewer?.scene.dispatchEvent({
          type: 'floor_group_hover_from_hierarchy',
          value: { groupIdentifier: group.identifier, isHovering: true },
        });
      }
    }, [group.identifier, group.type, viewer]);

    const onMouseLeave: MouseEventHandler<HTMLDivElement> = useCallback(() => {
      if (group.type === GroupType.Floor) {
        viewer?.scene.dispatchEvent({
          type: 'floor_group_hover_from_hierarchy',
          value: { groupIdentifier: group.identifier, isHovering: false },
        });
      }
    }, [group.identifier, group.type, viewer]);

    const onToggleFolderOpen = useCallback(
      (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        event.stopPropagation();
        onGroupOpenChange?.(group.identifier, !folderOpen);
      },
      [folderOpen, onGroupOpenChange, group.identifier],
    );
    // Aggregate visibility over LEAF annotations, descending into nested room
    // folders (their sketches count toward the parent floor's indicator). An
    // empty group counts as visible.
    const leafAnnotations = useMemo(() => collectLeafAnnotations(group), [group]);
    const computeGroupVisible = useCallback(
      () =>
        leafAnnotations.length === 0 ||
        leafAnnotations.some((annotation) => {
          try {
            return getAnnotationVisibleProperty(annotation.identifier).value !== false;
          } catch {
            return true;
          }
        }),
      [leafAnnotations],
    );
    const [visible, setVisible] = useState(computeGroupVisible);
    useEffect(() => {
      const recompute = () => setVisible(computeGroupVisible());
      recompute();
      const subscriptions = leafAnnotations.flatMap((annotation) => {
        try {
          return [getAnnotationVisibleProperty(annotation.identifier).subscribe(recompute)];
        } catch {
          return [];
        }
      });
      return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
    }, [leafAnnotations, computeGroupVisible]);

    const groupIcon = GROUP_ICONS_BY_TYPE[group.type] ?? FolderIcon;

    // Openings are nested under their linked rooms, never inside groups.
    // Also drop annotation types the active persona hides (e.g. rooms under a
    // floor while in the Layout persona, which shows only sketch objects).
    // Nested room folders are persona-filtered too (floorplan persona only).
    const visibleChildren = useMemo(
      () => {
        // Legacy OUTLINE tombstone: superseded by the floor sketch's merged
        // `role:'OUTLINE'` edges — hidden from the tree like from the
        // viewport (re-appears if the merge is undone).
        const floorSketch = group.type === GroupType.Floor ? viewer?.scene.wallSketches.find((s) => s.groupIdentifier === group.identifier) : undefined;
        const hasMergedOutline = !!floorSketch && floorSketch.getOutlineEdges().length > 0;
        return group.annotations.filter((a) => (isAnnotationContextGroup(a) ? !shouldHideGroupType(a.type) : a.type !== 'OPENING' && !(a.type === 'OUTLINE' && hasMergedOutline) && !shouldHideType(a.type)));
      },
      [group.annotations, group.type, group.identifier, viewer, shouldHideType, shouldHideGroupType],
    );

    const childrenContent = folderOpen ? (
      <div data-group-children={group.identifier} className={`flow-root${isDragging ? ' hidden' : ''}`}>
        {visibleChildren.flatMap((annotation, index) => {
          const elements: React.ReactNode[] = [];

          if (dropIndicator?.groupId === group.identifier && dropIndicator.index === index) {
            elements.push(<DropIndicatorLine indented nested={nested} key="drop-indicator" />);
          }

          if (isAnnotationContextGroup(annotation)) {
            // Nested room folder — recurse with a full sortable group row.
            elements.push(
              <AnnotationGroup
                project={project}
                group={annotation as AnnotationGroupProps['group']}
                key={annotation.identifier}
                isFirst={index === 0}
                isLast={index === visibleChildren.length - 1}
                onClickAnnotation={onClickAnnotation}
                rootIndex={index}
                isReadOnly={isReadOnly}
                dropIndicator={dropIndicator}
                isDragOver={isDragOver}
                isOpen={openGroups?.has(annotation.identifier) ?? true}
                onGroupOpenChange={onGroupOpenChange}
                openingsByRoomId={openingsByRoomId}
                openRooms={openRooms}
                onRoomOpenChange={onRoomOpenChange}
                openGroups={openGroups}
                nested
              />,
            );
            return elements;
          }

          elements.push(
            <Annotation
              project={project}
              type={annotation.type as AnnotationTypeName}
              rootActive={active}
              annotation={annotation}
              key={annotation.identifier}
              className={nested ? 'pl-24' : 'pl-16'}
              groupIdentifier={group.identifier}
              groupType={group.type}
              isFirst={index === 0}
              isLast={index === visibleChildren.length - 1}
              onClickAnnotation={onClickAnnotation}
              rootIndex={index}
              isReadOnly={isReadOnly}
              isDragOver={isDragOver}
              linkedOpeningIds={openingsByRoomId?.[annotation.identifier]}
              isRoomOpen={openRooms?.has(annotation.identifier) ?? false}
              onRoomOpenChange={onRoomOpenChange}
            />,
          );

          return elements;
        })}
        {dropIndicator?.groupId === group.identifier && dropIndicator.index >= visibleChildren.length && <DropIndicatorLine indented nested={nested} />}
      </div>
    ) : null;

    if (isDragging) {
      return childrenContent;
    }

    return (
      <>
        {nameEditable ? (
          nameInputComponent
        ) : (
          <ContextMenu
            popoverMenuDisabled={isReadOnly}
            buttonChild={
              <div ref={sortableRef} style={{ transition: sortableTransition }} {...sortableAttributes} {...sortableListeners} data-group-id={group.identifier} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
                {/* Nested folder rows indent to pl-16 — the same level as their
                    sibling annotation rows inside the floor (which use pl-16),
                    so floor-level annotations don't read as folder children. */}
                <SelectItemContent onClick={onClick} icon={groupIcon} active={active || isDragOver} selectStyle="primary" idle={!visible} className={nested ? 'relative pl-16' : 'relative pl-8'} onDoubleClick={onToggleNameEditable}>
                  <div className={`absolute flex items-center justify-center w-5 h-5 rounded ${nested ? 'left-9' : 'left-1'} hover:text-neon-green-400`} onClick={onToggleFolderOpen}>
                    {folderOpen ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
                  </div>
                  <div className="overflow-hidden">{group.name}</div>
                  <div className="mx-2 flex justify-end grow">{is2DContext && <Pill className="text-black bg-neon-green-500">2D</Pill>}</div>
                  <AnnotationsIconsWrapper identifier={group.identifier} visible={visible} group={group} project={project} />
                </SelectItemContent>
              </div>
            }
            panelClassName="divide-y"
          >
            {isMultiSelect ? <MultiSelectMenuItems /> : <ContextMenuItems group={group} isFirst={isFirst} isLast={isLast} rootIndex={rootIndex} onToggleNameEditable={onToggleNameEditable} />}
          </ContextMenu>
        )}

        {childrenContent}
      </>
    );
  },
  (prev, next) =>
    prev.group.identifier === next.group.identifier &&
    prev.group.name === next.group.name &&
    prev.group.type === next.group.type &&
    prev.group.index === next.group.index &&
    areGroupChildrenEqual(prev.group.annotations, next.group.annotations) &&
    prev.isFirst === next.isFirst &&
    prev.isLast === next.isLast &&
    prev.isReadOnly === next.isReadOnly &&
    prev.rootIndex === next.rootIndex &&
    prev.dropIndicator === next.dropIndicator &&
    prev.isDragOver === next.isDragOver &&
    prev.isOpen === next.isOpen &&
    prev.onClickAnnotation === next.onClickAnnotation &&
    prev.onGroupOpenChange === next.onGroupOpenChange &&
    prev.isDragging === next.isDragging &&
    prev.sortableTransition === next.sortableTransition &&
    prev.openingsByRoomId === next.openingsByRoomId &&
    prev.openRooms === next.openRooms &&
    prev.onRoomOpenChange === next.onRoomOpenChange &&
    prev.openGroups === next.openGroups &&
    prev.nested === next.nested,
);

export const areGroupChildrenEqual = (prev: AnnotationGroupProps['group']['annotations'], next: AnnotationGroupProps['group']['annotations']) => {
  if (prev === next) return true;
  if (prev.length !== next.length) return false;
  return prev.every((a, i) => {
    const b = next[i];
    if (a.identifier !== b.identifier || a.type !== b.type) return false;
    // Nested group children (a floor's room folders) render recursively FROM
    // this row — anything inside them (children added/removed, rename) must
    // re-render it. Structural sharing keeps node identity stable when truly
    // unchanged, so reference equality is the correct check. Leaf annotation
    // rows subscribe to their own data and stay identifier/type-compared.
    if (isAnnotationContextGroup(a) || isAnnotationContextGroup(b)) return a === b;
    return true;
  });
};

/** Thin sortable wrapper — subscribes to SortableContext (dnd-kit) and passes
 *  sortable props down. When the items list changes, only this wrapper re-renders,
 *  not the heavy AnnotationGroupContent tree. */
const AnnotationGroup_: React.FC2<AnnotationGroupProps> = (props) => {
  const { attributes, listeners, setNodeRef, transition, isDragging } = useSortable({
    id: props.group.identifier,
    data: {
      type: 'group' as const,
      identifier: props.group.identifier,
      // Nested room folders order within their parent floor's context.
      groupIdentifier: props.group.parentIdentifier ?? undefined,
      index: props.group.index,
    },
    disabled: props.isReadOnly,
  });

  return <AnnotationGroupContent {...props} sortableRef={setNodeRef} sortableTransition={transition} sortableAttributes={attributes} sortableListeners={listeners} isDragging={isDragging} />;
};

export const AnnotationGroup = memo(AnnotationGroup_);
